import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Timer, Ban, Wallet } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

export default function Responsible() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const [limit, setLimit] = useState(user?.responsible?.daily_spend_limit ?? "");
  const [cooldown, setCooldown] = useState(1);
  const [excludeDays, setExcludeDays] = useState(7);

  const save = async (payload, msg) => {
    try { await api.post("/account/responsible-gaming", payload); await refresh(); toast.success(msg); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const resp = user?.responsible || {};

  return (
    <div className="p-4 lg:p-8 max-w-4xl">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-2 flex items-center gap-3"><ShieldAlert className="w-8 h-8 text-emerald-400" /> {t("responsible_title")}</h1>
      <p className="text-gray-400 mb-8">{t("responsible_sub")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 border border-white/10">
          <div className="flex items-center gap-2 mb-3"><Wallet className="w-5 h-5 text-amber-400" /><h3 className="font-semibold">{t("daily_limit")}</h3></div>
          <p className="text-sm text-gray-500 mb-4">Cap how much you can spend on coin packs per day.</p>
          <input data-testid="spend-limit-input" type="number" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="$ amount"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 mb-3 outline-none focus:border-amber-500 transition-colors duration-200 font-mono" />
          <button data-testid="spend-limit-modal-trigger" onClick={() => save({ daily_spend_limit: parseFloat(limit) || 0 }, "Daily spend limit updated")}
            className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 font-semibold transition-colors duration-200">{t("set_limit")}</button>
          {resp.daily_spend_limit != null && <p className="text-xs text-gray-500 mt-2">Current: ${resp.daily_spend_limit}</p>}
        </div>

        <div className="glass rounded-2xl p-6 border border-white/10">
          <div className="flex items-center gap-2 mb-3"><Timer className="w-5 h-5 text-cyan-400" /><h3 className="font-semibold">{t("cooldown")}</h3></div>
          <p className="text-sm text-gray-500 mb-4">Pause play for a set number of hours.</p>
          <select value={cooldown} onChange={(e) => setCooldown(parseInt(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 mb-3 text-gray-300">
            {[1, 6, 12, 24].map((h) => <option key={h} value={h}>{`${h} hour${h > 1 ? "s" : ""}`}</option>)}
          </select>
          <button data-testid="cooldown-button" onClick={() => save({ cooldown_hours: cooldown }, `Cooldown set for ${cooldown}h`)}
            className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 font-semibold transition-colors duration-200">{t("start_cooldown")}</button>
        </div>

        <div className="glass rounded-2xl p-6 border border-red-500/20 md:col-span-2">
          <div className="flex items-center gap-2 mb-3"><Ban className="w-5 h-5 text-red-400" /><h3 className="font-semibold text-red-300">{t("self_exclusion")}</h3></div>
          <p className="text-sm text-gray-500 mb-4">Block yourself from skill games for a period. This cannot be undone until it expires.</p>
          <div className="flex gap-3">
            <select value={excludeDays} onChange={(e) => setExcludeDays(parseInt(e.target.value))} className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-gray-300">
              {[7, 30, 90, 180].map((d) => <option key={d} value={d}>{`${d} days`}</option>)}
            </select>
            <button data-testid="self-exclude-button" onClick={() => save({ self_exclude_days: excludeDays }, `Self-excluded for ${excludeDays} days`)}
              className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 font-semibold hover:bg-red-500/30 transition-colors duration-200">{t("self_exclude")}</button>
          </div>
          {resp.self_excluded_until && <p className="text-xs text-red-300 mt-3">Excluded until {new Date(resp.self_excluded_until).toLocaleString()}</p>}
        </div>
      </div>
    </div>
  );
}
